import type { AxiosResponse } from 'axios';
import { getAxiosClient } from '~/api/common';
import { getProject as apiGetProject, saveParcelDetails as apiSaveParcelDetails } from '~/api/projects2';
import { getParcelDetails as apiGetParcelDetails } from '~/api/title2';
import { getReasonForError } from '~/common/getReasonForError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import sendEvent from '~/common/sendEvent';
import type { Project, TitleJson } from '~/common/types';
import { Notify } from '~/components2/Notify';
import getConfig from '~/config';
import store, { type ParcelDetails } from '~/pages/projectDetails/landRights/store';
import { DocType, docTypeMap, OrderStep, ReportOrderError } from '~/pages/projectDetails/landRights/types';

const config = getConfig();
const axios = getAxiosClient();

const REPORT_STATUSES_HEADER = 'cg-report-statuses';

function parseReportStatuses(headers: Record<string, unknown>): Record<string, string> {
  const raw = headers[REPORT_STATUSES_HEADER];
  return typeof raw === 'string' && raw ? JSON.parse(raw) : {};
}

function isOk(status: number): boolean {
  return status >= 200 && status < 300;
}

// Title zip routes ending in `Download` answer with JSON statuses plus a signed attachment URL instead of the zip itself.
const SIGNED_URL_ROUTE_SUFFIX = 'Download';

interface TitleZipResponse {
  status: number;
  statuses: Record<string, string>;
  /** Hands the zip to the browser; `filename` names the in-page blob, while a signed URL carries its own name. */
  save: (filename: string) => void;
}

function readTitleZip(res: AxiosResponse, viaSignedUrl: boolean): TitleZipResponse {
  if (viaSignedUrl) {
    const body = (res.data ?? {}) as { statuses?: Record<string, string>; download?: { url: string } | null };
    return {
      status: res.status,
      statuses: body.statuses ?? {},
      save: () => {
        if (body.download) window.location.href = body.download.url;
      },
    };
  }
  return {
    status: res.status,
    statuses: parseReportStatuses(res.headers),
    save: (filename) => saveBlob(res.data, filename),
  };
}

function saveBlob(data: BlobPart, filename: string) {
  const file = new Blob([data], { type: 'octet/stream' });
  const url = window.URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 100);
}

export type LandRightsReportType = Exclude<DocType, DocType.PrelimTitleReport> | 'titleDocument';

export interface LandRightsEventDetail {
  version: 1;
  source: 'landRights';
  reportType: LandRightsReportType;
  apn?: string;
  projectId?: string;
  docId?: string;
}

const LAND_RIGHTS_EVENT_DETAIL_VERSION = 1 as const;
const LAND_RIGHTS_EVENT_DETAIL_SOURCE = 'landRights' as const;

export function buildLandRightsParcelReportDetail(
  docType: DocType,
  apn: string,
  projectId: string,
): LandRightsEventDetail {
  if (docType === DocType.PrelimTitleReport) {
    throw new Error('Prelim title reports are not logged through /logEvent');
  }

  return {
    version: LAND_RIGHTS_EVENT_DETAIL_VERSION,
    source: LAND_RIGHTS_EVENT_DETAIL_SOURCE,
    reportType: docType,
    apn,
    projectId,
  };
}

async function saveParcelDetails({ projectId, parcelDetails }: { projectId: string; parcelDetails: any[] }) {
  try {
    await apiSaveParcelDetails(projectId, parcelDetails);
  } catch (e) {
    console.error(e);
    Notify.error({
      title: '⚠️',
      message: 'Error saving parcel details.',
    });
  }
}

export const getParcelIdsFromSelectedIds = async (selectedIds: Set<string>) => {
  return Array.from(selectedIds).map((s) => s.split('.')[1]);
};

export async function getParcelDetails({
  parcelId,
  projectId,
  setProjectData,
  project,
  parcelDetails,
}: {
  parcelId?: string;
  projectId: string;
  setProjectData: (project: any) => void;
  project?: Project;
  parcelDetails: any[];
}) {
  if (!parcelId) return;
  try {
    const res = await apiGetParcelDetails({ parcelId, projectId });

    const details = {
      parcelId: res.parcel_id,
      countyId: res.county_id,
      rausaId: res.rausa_id,
      countyName: res.county_name,
      muniName: res.muni_name,
      stateAbbr: res.state_abbr,
      addrNumber: res.addr_number,
      addrStreetName: res.addr_street_name,
      addrStreetType: res.addr_street_type,
      physcity: res.physcity,
      physzip: res.physzip,
      censusZip: res.census_zip,
      mailZipcode: res.mail_zipcode,
      owner: res.owner,
      mailName: res.mail_name,
      mailAddress1: res.mail_address1,
      mailAddress3: res.mail_address3,
      mktValLand: res.mkt_val_land,
      mktValBldg: res.mkt_val_bldg,
      bldgSqft: res.bldg_sqft,
      yearBuilt: res.year_built,
      landUseCode: res.land_use_code,
      landUseClass: res.land_use_class,
      muniId: res.muni_id,
      schoolDistId: res.school_dist_id,
      acreageCalc: res.acreage_calc,
      latitude: res.latitude,
      longitude: res.longitude,
      geomAsWkt: res.geom_as_wkt,
      robustId: res.robust_id,
      uspsResidential: res.usps_residential,
      elevation: res.elevation,
      titleJson: res.titleJson,
    };

    // Save parcel details to the project
    saveParcelDetails({ projectId, parcelDetails });
    setProjectData({
      ...project,
      parcelDetails: [...parcelDetails, details],
    });
  } catch (e) {
    console.error(e);
    Notify.error({
      title: '⚠️',
      message: 'Error loading parcel details.',
    });
  }
}

// Prefer the assessor-of-record mailing ZIP because First American Title's
// records key off the assessor data; the census ZIP is geocoder-derived and
// can disagree, which surfaces as opaque 500s from the FAT API.
export function pickFatZip(parcel: { mailZipcode?: string | null; censusZip?: string | null }): string | undefined {
  return parcel.mailZipcode || parcel.censusZip || undefined;
}

export interface ParcelRequest {
  apn: string;
  projectId: string;
  zip: string;
  mailZip?: string;
  censusZip?: string;
  lat?: string;
  lng?: string;
}

interface ResolvedParcel {
  parcelId?: string;
  zip?: unknown;
  mailZipcode?: string | null;
  censusZip?: string | null;
  latitude?: string;
  longitude?: string;
}

// Shape the FAT request payload from already-resolved parcels. lat/lng let the server
// reverse-geocode a ZIP; both assessor ZIPs let it retry FAT with another one.
export function toParcelRequests(parcels: Array<ResolvedParcel | undefined>, projectId: string): ParcelRequest[] {
  return parcels.reduce<ParcelRequest[]>((acc, p) => {
    if (p?.parcelId && typeof p.zip === 'string') {
      acc.push({
        apn: p.parcelId,
        zip: p.zip,
        projectId,
        ...(p.mailZipcode ? { mailZip: p.mailZipcode } : {}),
        ...(p.censusZip ? { censusZip: p.censusZip } : {}),
        ...(p.latitude ? { lat: p.latitude } : {}),
        ...(p.longitude ? { lng: p.longitude } : {}),
      });
    }
    return acc;
  }, []);
}

async function geocodeZipcodeFromLatLng(lat?: string, lng?: string) {
  if (!lat || !lng) throw new Error('Missing lat and/or lng');
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=postcode&access_token=${config.mapboxToken}`;

  return fetch(url).then((res) => {
    if (!res.ok) throw new Error('Network error');
    return res.json();
  });
}

async function refreshProjectData(projectId: string, setProject: (project: Project) => void) {
  const project = await apiGetProject(projectId);
  setProject(project);
}

interface BuildParcelRequestProps {
  type: DocType;
  projectId: string;
  parcelDetails: ParcelDetails[];
  selectedIds: Set<string>;
  setIsOpen: (isOpen: boolean) => void;
  setProject: (project: Project) => void;
  currentOrganizationId: string;
  isSandbox: boolean;
  viaSignedUrl?: boolean;
}

function reasonForReportStatus(status: number): string | undefined {
  if (status === 502) return 'title_vendor_failed';
  if (status === 404) return 'no_reports_available';
  return undefined;
}

export async function buildParcelRequest({
  type,
  projectId,
  parcelDetails,
  selectedIds,
  setIsOpen,
  setProject,
  currentOrganizationId,
  isSandbox,
  viaSignedUrl = false,
}: BuildParcelRequestProps) {
  store.resetOrderSummary();

  // sort selected parcel attributes into has zipcode and needs zipcode
  const hasZipCode: ResolvedParcel[] = [];
  const needsZipCode: ParcelDetails[] = [];
  parcelDetails.forEach((parcel) => {
    const maybeIds = Array.from(selectedIds).map((s) => s.split('.')[1]);
    const uniqueId = parcel.uniqueId.split('.')[1];
    if (maybeIds.includes(uniqueId)) {
      const zip = pickFatZip(parcel);
      if (zip) {
        hasZipCode.push({
          parcelId: parcel.parcelId!,
          zip,
          mailZipcode: parcel.mailZipcode,
          censusZip: parcel.censusZip,
          latitude: parcel.latitude,
          longitude: parcel.longitude,
        });
      } else {
        needsZipCode.push(parcel);
      }
    }
  });

  // build array of requests for parcels that need zipcodes
  const missingZipCodeRequests = needsZipCode.map(async ({ parcelId, latitude, longitude }) => {
    const { features } = await geocodeZipcodeFromLatLng(latitude, longitude);
    return {
      parcelId,
      zip: features[0].text,
      latitude,
      longitude,
    };
  });

  // make requests for missing zipcodes
  const missingZipCodeResults = await Promise.allSettled(missingZipCodeRequests);

  // filter out errors and show notifications
  const geocodedResults = missingZipCodeResults
    .filter((result) => {
      if (result.status === 'fulfilled') {
        return true;
      } else {
        const message = `
          <div>
            Error geocoding zipcode. ${result.reason.message}.
          </div>
            `;

        Notify.error({
          title: '⚠️',
          message,
        });
        return false;
      }
    })
    .map((result) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }
      return undefined;
    });

  // recombine parcel attributes to send to report endpoint
  const parcelRequests: ParcelRequest[] = toParcelRequests([...geocodedResults, ...hasZipCode], projectId);
  const eventProps = { project_id: projectId, organization_id: currentOrganizationId || undefined, type };

  const reportDocsUrl = `/v1/title/getReportDocs${viaSignedUrl ? SIGNED_URL_ROUTE_SUFFIX : ''}`;
  let url: string = '';
  switch (type) {
    case 'chainOfTitleReport':
      url = '/v1/title/getTitleReport';
      break;
    case 'mapReport':
      url = `${reportDocsUrl}?reportType=AssessorMap`;
      break;
    case 'openLienReport':
      url = `${reportDocsUrl}?reportType=OpenLienReport`;
      break;
    case 'transactionHistoryReport':
      url = `${reportDocsUrl}?reportType=TransactionHistoryReport`;
      break;
  }

  return axios
    .post(url, parcelRequests, {
      responseType: viaSignedUrl ? 'json' : 'blob',
      // 404 and 502 still carry per-parcel statuses, so they fall through to the per-parcel notifications.
      validateStatus: (status) => isOk(status) || status === 429 || status === 404 || status === 502,
    })
    .then(async (res) => {
      const zip = readTitleZip(res, viaSignedUrl);
      const reportStatuses = zip.statuses;

      if (res.status === 429 || Object.values(reportStatuses).includes('noremaining')) {
        throw new Error('overTrial');
      }

      if (isSandbox) {
        throw new Error('sampleReport');
      }

      // If a title report sent to the jobs server, catch it here and handle
      if (type === DocType.ChainOfTitleReport) {
        return {
          zipFile: undefined,
          reportStatuses: undefined,
          skipZipDownload: true,
        };
      }

      // A 502/404 without per-parcel statuses came from the gateway, not the title vendor.
      const hasStatuses = Object.keys(reportStatuses).length > 0;
      const failureReason = hasStatuses ? reasonForReportStatus(res.status) : undefined;

      // throw error if all reports fail
      if (!Object.values(reportStatuses).find((status) => status !== 'Failed')) {
        throw new ReportOrderError(failureReason ?? (hasStatuses ? 'all_reports_failed' : String(res.status)));
      }

      // get zip file if a report was generated successfully
      const zipFile = Object.values(reportStatuses).find((status) => status === 'Ready') ? zip : null;
      return {
        zipFile,
        reportStatuses,
        failureReason,
      };
    })
    .then(({ zipFile, reportStatuses, skipZipDownload, failureReason }) => {
      if (skipZipDownload) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.landRightsDataDownloaded, {
          ...eventProps,
          doc_count: parcelRequests.length,
        });
        Notify.info({
          title: '✔️',
          message: 'You will receive your title report in your email inbox.',
        });
        setIsOpen(false);
        return;
      }

      const notAvailableParcels: string[] = [];
      // download zip file if one was sent
      if (zipFile !== null && zipFile !== undefined) {
        zipFile.save(`parcel-${type}${parcelRequests.length > 1 ? 's' : ''}-${Date.now()}.zip`);
      }

      if (typeof reportStatuses === 'undefined') {
        throw new Error('reportStatuses is undefined');
      }
      let readyCount = 0;
      let failedCount = 0;
      const parcelsAvailableToDownload: string[] = [];
      const vendorFailedParcels: string[] = [];
      const failedParcels: string[] = [];
      // display errors and log responses
      Object.entries(reportStatuses).forEach(([apn, val]) => {
        switch (val) {
          case 'Ready':
            // send log event
            sendEvent(
              'parcel_report_download',
              buildLandRightsParcelReportDetail(type, apn, projectId),
              currentOrganizationId,
            );
            readyCount++;
            parcelsAvailableToDownload.push(apn);
            break;

          case 'NotAvailable':
            notAvailableParcels.push(apn);
            //show notification
            Notify.error({
              title: '⚠️',
              message: `${
                type === DocType.OpenLienReport
                  ? `No open liens were found for parcel #${apn}`
                  : `${docTypeMap[type]} for parcel #${apn} is not available`
              }`,
            });

            break;

          default: {
            // show error
            const message = `
              <div>We were unable to download your ${docTypeMap[type]} for parcel #${apn}.
                Please contact
                <a
                  href="mailto:support@civilgrid.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-50 underline hover:text-blue-60"
                >
                  Support
                </a>
                .
              </div>
            `;

            Notify.error({
              title: '⚠️',
              message:
                failureReason === 'title_vendor_failed'
                  ? `Title vendor could not produce ${docTypeMap[type]} for parcel #${apn}.`
                  : message,
            });

            // send log event
            sendEvent(
              'parcel_report_download_error',
              buildLandRightsParcelReportDetail(type, apn, projectId),
              currentOrganizationId,
            );
            if (failureReason === 'title_vendor_failed') {
              vendorFailedParcels.push(apn);
            } else {
              failedParcels.push(apn);
            }
            failedCount++;
            break;
          }
        }
      });
      if (readyCount > 0) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.landRightsDataDownloaded, { ...eventProps, doc_count: readyCount });
      }
      if (failedCount > 0) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.landRightsDataDownloadFailed, {
          ...eventProps,
          doc_count: failedCount,
          reason: failureReason ?? 'partial_failure',
        });
      }
      if (notAvailableParcels.length > 0) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.landRightsDataDownloadFailed, {
          ...eventProps,
          doc_count: notAvailableParcels.length,
          reason: 'no_reports_available',
        });
      }
      // show finished modal status
      store.updatenNotAvailableParcels(notAvailableParcels);
      refreshProjectData(projectId, setProject);
      store.updateOrderSummary({
        parcelsAvailableToDownload,
        vendorFailedParcels,
        failedParcels,
        orderedParcelCount: Object.keys(reportStatuses).length,
      });
      store.updateDocumentOrderStep(
        failureReason === 'title_vendor_failed' && readyCount === 0 ? OrderStep.VendorError : OrderStep.Complete,
      );
    })
    .catch((err) => {
      if (err.message === 'sampleReport') {
        store.updateDocumentOrderStep(OrderStep.Complete);
      } else if (err.message === 'overTrial') {
        store.updateDocumentOrderStep(OrderStep.OverTrial);
      } else {
        captureTypedPosthogEvent(POSTHOG_EVENTS.landRightsDataDownloadFailed, {
          ...eventProps,
          doc_count: parcelRequests.length,
          reason: err instanceof ReportOrderError ? err.reason : getReasonForError(err),
        });
        store.updateDocumentOrderStep(
          err instanceof ReportOrderError && err.reason === 'title_vendor_failed'
            ? OrderStep.VendorError
            : OrderStep.Error,
        );
      }
    });
}

interface ReprintTitleProps {
  parcelId: string;
  zip: string;
  projectId: string;
}

export const reprintTitle = async ({ parcelId, zip, projectId }: ReprintTitleProps) => {
  axios
    .post('/v1/title/getTitleReport', [{ apn: parcelId, zip, projectId }], {
      validateStatus: (status) => isOk(status) || status === 429,
    })
    .then((res) => {
      if (res.status === 429) {
        throw new Error('overTrial');
      }
    })
    .then(() => {
      Notify.info({
        title: '✔️',
        message: 'You will receive your title report in your email inbox.',
      });
    })
    .catch((err) => {
      if (err.message === 'overTrial') {
        store.updateDocumentOrderStep(OrderStep.OverTrial);
      } else {
        Notify.error({
          title: '⚠️',
          message: 'There was an error locating this document. Please contact Support.',
        });
      }
    });
};

interface OrderTitleReportProps {
  projectId?: string;
  docParams?: {
    DocId: string;
    CmtId: string;
  };
  currentOrganizationId?: string;
  viaSignedUrl?: boolean;
}

interface OrderMultipleTitleReportProps {
  projectId?: string;
  docsParams?: {
    DocId: string;
    CmtId: string;
  }[];
  viaSignedUrl?: boolean;
}

export const orderTitleReport = ({
  projectId,
  docParams,
  viaSignedUrl = false,
}: OrderTitleReportProps): Promise<boolean> => {
  if (!projectId || !docParams) return Promise.resolve(false);

  Notify.info({
    title: '✔️',
    message: 'Your file will download momentarily.',
  });

  return axios
    .get(`/v1/title/getDocumentById${viaSignedUrl ? SIGNED_URL_ROUTE_SUFFIX : ''}`, {
      params: { projectId, docId: docParams.DocId, cmtId: docParams.CmtId },
      responseType: viaSignedUrl ? 'json' : 'blob',
      validateStatus: (status) => isOk(status) || status === 429,
    })
    .then((res) => {
      const zip = readTitleZip(res, viaSignedUrl);
      const reportStatuses = zip.statuses;

      if (res.status === 429 || Object.values(reportStatuses).includes('noremaining')) {
        throw new Error('overTrial');
      }

      const zipFile = Object.values(reportStatuses).find((status) => status === 'success') ? zip : null;

      return zipFile;
    })

    .then((zipFile) => {
      // download zip file if one was sent
      if (zipFile !== null) {
        zipFile.save(`document-${docParams.DocId}.zip`);
        return true;
      }
      return false;
    })
    .catch((err) => {
      if (err.message === 'overTrial') {
        store.updateDocumentOrderStep(OrderStep.OverTrial);
      } else {
        store.updateDocumentOrderStep(OrderStep.Error);
        Notify.error({
          title: '⚠️',
          message: 'There was an error locating this document. Please contact Support.',
        });
      }
      return false;
    });
};

interface DocIdParam {
  DocId: string;
  CmtId: string;
}

/** Row key for the documents Table — DocId alone isn't unique since the same DocId can appear with different CmtIds. */
export function docRowKey({ DocId, CmtId }: DocIdParam): string {
  return `${DocId}:${CmtId}`;
}

/** Resolves a Table `RowSelectionState` (keyed by docRowKey) to the selected docs' id params. */
export function getSelectedDocParams<T extends DocIdParam>(rowSelection: Record<string, boolean>, docs: T[]): T[] {
  return docs.filter((doc) => rowSelection[docRowKey(doc)]);
}

/** Combines a document's grantor/grantee into the Table's searchable Parties value. */
export function formatParties(party1?: string, party2?: string): string {
  return [party1, party2].filter(Boolean).join(' - ');
}

export function getDocRowLabel(
  row: Pick<TitleJson, 'DocType' | 'Date' | 'Party1' | 'Party2' | 'DocId' | 'CmtId'>,
): string {
  const parts = [row.DocType, row.Date, formatParties(row.Party1, row.Party2)].filter(Boolean).join(' ');
  return parts ? `document ${parts} (${docRowKey(row)})` : `document ${docRowKey(row)}`;
}

export interface TitleReportDownloadCounts {
  successCount: number;
  failureCount: number;
}

export const orderMultipleTitleReports = ({
  projectId,
  docsParams,
  viaSignedUrl = false,
}: OrderMultipleTitleReportProps): Promise<TitleReportDownloadCounts> => {
  if (!projectId || !docsParams || docsParams.length === 0) {
    return Promise.resolve({ successCount: 0, failureCount: 0 });
  }

  const fileCopy = docsParams.length > 1 ? 'files' : 'file';

  Notify.info({
    title: '✔️',
    message: `Your ${fileCopy} will download momentarily.`,
  });

  return axios
    .post(
      `/v1/title/getDocumentsByIds${viaSignedUrl ? SIGNED_URL_ROUTE_SUFFIX : ''}`,
      {
        projectId,
        docsParams: docsParams.map(({ DocId, CmtId }) => ({ docId: DocId, cmtId: CmtId })),
      },
      {
        responseType: viaSignedUrl ? 'json' : 'blob',
        validateStatus: (status) => isOk(status) || status === 429,
      },
    )
    .then((res) => {
      const zip = readTitleZip(res, viaSignedUrl);
      const reportStatuses = zip.statuses;

      if (res.status === 429 || Object.values(reportStatuses).includes('noremaining')) {
        throw new Error('overTrial');
      }

      const zipFile = Object.values(reportStatuses).find((status) => status === 'Ready') ? zip : null;
      return { zipFile, reportStatuses };
    })
    .then(({ zipFile, reportStatuses }) => {
      const successCount = Object.values(reportStatuses).filter((status) => status === 'Ready').length;
      const failureCount = Object.keys(reportStatuses).length - successCount;

      if (zipFile === null) {
        store.updateDocumentOrderStep(OrderStep.Error);
        Notify.error({
          title: '⚠️',
          message: 'There was an error locating this document. Please contact Support.',
        });
        return { successCount, failureCount };
      }

      zipFile.save(`documents-${Date.now()}.zip`);
      return { successCount, failureCount };
    })
    .catch((err) => {
      if (err.message === 'overTrial') {
        store.updateDocumentOrderStep(OrderStep.OverTrial);
      } else {
        store.updateDocumentOrderStep(OrderStep.Error);
        Notify.error({
          title: '⚠️',
          message: 'There was an error locating this document. Please contact Support.',
        });
      }
      return { successCount: 0, failureCount: docsParams.length };
    });
};
